<script setup>
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import { useClientType } from '@/composables/useClientType';

/**
 * 복지찬스 이용 안내(가이드) 화면 (시연 v2.4 단계6).
 * - 모바일: 풀스크린 / PC: 가운데 팝업 (CancelScreen과 동일 분기)
 * - 완료 팝업 "이용 안내 보러가기" / 구독정보 시트 "이용 가이드"에서 진입
 * - 뒤로/사주등록 emit만 발생 — 실제 이동은 부모가 결정
 */
const { isMobileUi } = useClientType();
const emit = defineEmits(['close', 'register-saju']);
</script>

<template>
  <Teleport to="body">
    <div v-if="!isMobileUi" class="swg-backdrop" @click="emit('close')" />
    <div class="swg-screen" :class="isMobileUi ? 'swg-screen--mobile' : 'swg-screen--pc'">
      <div class="swg-topbar">
        <button
          v-if="isMobileUi"
          type="button"
          class="swg-iconbtn"
          aria-label="뒤로"
          @click="emit('close')"
        >
          <IconHeaderBack />
        </button>
        <span class="swg-topbar-title">이용 안내</span>
        <button
          v-if="!isMobileUi"
          type="button"
          class="swg-iconbtn swg-iconbtn--close"
          aria-label="닫기"
          @click="emit('close')"
        ></button>
      </div>

      <div class="swg-content">
        <div class="swg-intro">
          <h1>낭만만세력,<br />이렇게 이용하세요</h1>
          <p>
            저장한 사주는 모두 <span class="swg-intro-hl">[낭만분석]</span>에서 확인할 수 있어요.
          </p>
        </div>

        <div class="swg-card">
          <div class="swg-card-head">
            <div class="swg-num">1</div>
            <h3>먼저 내 사주를 등록해주세요</h3>
          </div>
          <ul>
            <li>
              사주를 등록하면 <span class="swg-em">매일 홈에서 해석 서비스</span>를 받을 수 있어요
            </li>
            <li>등록한 사주로 다양한 해석 서비스가 제공돼요</li>
          </ul>
        </div>

        <div class="swg-card">
          <div class="swg-card-head">
            <div class="swg-num">2</div>
            <h3>정기구독 중에는 낭만포인트 전환이 필요 없어요</h3>
          </div>
          <ul>
            <li>구독만으로 대부분의 서비스를 자유롭게 이용할 수 있어요</li>
            <li>
              단, <span class="swg-em">작명 서비스</span>는 구독에 포함되지 않아 낭만포인트 전환이
              필요해요
            </li>
            <li>상단 <span class="swg-hl">'포인트 전환'</span> 버튼에서 언제든 전환할 수 있어요</li>
          </ul>
        </div>

        <button type="button" class="swg-cta" @click="emit('register-saju')">
          내 사주 등록하러 가기 <span>→</span>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.swg-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.5);
  animation: swg-fade 0.2s ease-out;
}

.swg-screen {
  position: fixed;
  z-index: 1101;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f7f8fa;
  color: #1f2937;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Malgun Gothic',
    'Pretendard Variable', sans-serif;
  letter-spacing: -0.3px;
  word-break: keep-all;
}

.swg-screen--mobile {
  inset: 0;
  padding-top: var(--safe-area-top, 0px);
}

.swg-screen--pc {
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 48px);
  max-width: 420px;
  max-height: 86vh;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: swg-scale-in 0.22s ease-out;
}

.swg-topbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 16px 16px 24px;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
}

/* 모바일(풀스크린): 좌우 동일 16px */
.swg-screen--mobile .swg-topbar {
  padding: 16px;
}

.swg-iconbtn {
  // width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #374151;
  cursor: pointer;

  :deep(.icon) {
    display: flex;
    line-height: 0;
  }

  /* PC 팝업: 아이콘 닫기는 우측 상단 */
  &--close {
    margin-left: auto;
    background: url('@manseImages/icon_close_1.svg') no-repeat center;
  }
}

.swg-topbar-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 32px;
}

.swg-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 16px calc(var(--safe-area-bottom, 0px) + 32px);
}

/* PC 팝업: 좌우 패딩 32px */
.swg-screen--pc .swg-content {
  padding-left: 32px;
  padding-right: 32px;
}

.swg-intro {
  margin-bottom: 18px;

  h1 {
    margin-bottom: 8px;
    font-size: 21px;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.4;
  }

  p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
  }
}

.swg-intro-hl {
  color: #1e3a5f;
  font-weight: 600;
}

.swg-card {
  margin-bottom: 12px;
  padding: 17px 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.swg-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  h3 {
    font-size: 14.5px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.4;
  }
}

.swg-num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #1e3a5f;
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
}

.swg-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.swg-card li {
  position: relative;
  padding-left: 14px;
  font-size: 14px;
  color: #374151;
  line-height: 1.55;

  & + li {
    margin-top: 7px;
  }

  &::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 8px;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #9ca3af;
  }
}

.swg-em {
  color: #1f2937;
  font-weight: 700;
}

.swg-hl {
  color: #1e3a5f;
  font-weight: 600;
}

.swg-cta {
  width: 100%;
  margin-top: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  background: #1e3a5f;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.1);
  transition: background 0.15s;

  &:hover {
    background: #244873;
  }
}

@keyframes swg-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes swg-scale-in {
  from {
    transform: translate(-50%, -50%) scale(0.94);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}
</style>
